import { apiUrl } from './publicConfig'

// Keep the anonymous transport independent from the authenticated Apollo client.
const endpoint = `${apiUrl}/layouts/graphql`

const layoutFields = `
  layout_uuid
  name
  integrations
  revision
  updated_at
`

const PUBLIC_LAYOUT_QUERY = `
  query PublicLayout($layout_uuid: ID!) {
    publicLayout(layout_uuid: $layout_uuid) {
      ${layoutFields}
    }
  }
`

const PUBLIC_LAYOUT_STATUS_QUERY = `
  query PublicLayoutStatus($layout_uuid: ID!) {
    publicLayoutStatus(layout_uuid: $layout_uuid)
  }
`

const PUBLIC_LAYOUT_CREATE = `
  mutation PublicLayoutCreate($input: PublicLayoutCreateInput!) {
    publicLayoutCreate(input: $input) {
      ${layoutFields}
    }
  }
`

const PUBLIC_LAYOUT_UPDATE = `
  mutation PublicLayoutUpdate($input: PublicLayoutUpdateInput!) {
    publicLayoutUpdate(input: $input) {
      ${layoutFields}
    }
  }
`

const PUBLIC_LAYOUT_DELETE = `
  mutation PublicLayoutDelete($input: PublicLayoutDeleteInput!) {
    publicLayoutDelete(input: $input)
  }
`

const PUBLIC_LAYOUT_DELIVER_LINK = `
  mutation PublicLayoutDeliverLink($input: PublicLayoutDeliverLinkInput!) {
    publicLayoutDeliverLink(input: $input) {
      email_sent
    }
  }
`

export class PublicLayoutRequestError extends Error {
  constructor (message, { retryAfter = null, code = null } = {}) {
    super(message)
    this.name = 'PublicLayoutRequestError'
    this.retryAfter = retryAfter
    this.code = code
  }
}

const retryAfterSeconds = (response, result) => {
  const header = Number.parseInt(response.headers?.get('Retry-After'), 10)
  const extension = Number.parseInt(result?.errors?.[0]?.extensions?.retry_after, 10)
  if (Number.isFinite(header) && header > 0) return header
  if (Number.isFinite(extension) && extension > 0) return extension
  return null
}

const request = async (query, variables) => {
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables })
  })
  let result
  try {
    result = await response.json()
  } catch {
    throw new PublicLayoutRequestError('The layout service did not return a valid response.')
  }

  if (!response.ok || result.errors?.length) {
    const retryAfter = retryAfterSeconds(response, result)
    const code = result.errors?.[0]?.extensions?.code || null
    throw new PublicLayoutRequestError(
      result.errors?.[0]?.message || 'The layout request failed.',
      { retryAfter, code }
    )
  }
  return result.data
}

export const loadPublicLayout = layoutUuid => request(
  PUBLIC_LAYOUT_QUERY,
  { layout_uuid: layoutUuid }
).then(data => data.publicLayout)

// Only asked after publicLayout comes back null. Resolves to AVAILABLE | CLAIMED | UNAVAILABLE.
export const loadPublicLayoutStatus = layoutUuid => request(
  PUBLIC_LAYOUT_STATUS_QUERY,
  { layout_uuid: layoutUuid }
).then(data => data.publicLayoutStatus)

export const createPublicLayout = layout => request(
  PUBLIC_LAYOUT_CREATE,
  {
    input: {
      name: layout.name,
      integrations: layout.integrations
    }
  }
).then(data => data.publicLayoutCreate)

export const updatePublicLayout = (capability, layout) => request(
  PUBLIC_LAYOUT_UPDATE,
  {
    input: {
      layout_uuid: capability.layoutUuid,
      name: layout.name,
      integrations: layout.integrations
    }
  }
).then(data => data.publicLayoutUpdate)

export const deletePublicLayout = capability => request(
  PUBLIC_LAYOUT_DELETE,
  { input: { layout_uuid: capability.layoutUuid } }
).then(data => data.publicLayoutDelete)

export const deliverPublicLayoutLink = (capability, email) => request(
  PUBLIC_LAYOUT_DELIVER_LINK,
  {
    input: {
      layout_uuid: capability.layoutUuid,
      email
    }
  }
).then(data => data.publicLayoutDeliverLink)
